import { loadStripe, Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { stripeConfigService } from './stripeConfigService';
import { 
  StripeService, 
  PaymentIntentResult, 
  PaymentInfo, 
  CustomerInfo, 
  RefundInfo,
  PaymentStatus,
  PaymentMethod
} from '../types';

/**
 * StripeService - Handles all Stripe payment operations
 * Integrates with admin-configurable Stripe keys
 */
class StripeServiceImpl implements StripeService {
  private static instance: StripeServiceImpl;
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): StripeServiceImpl {
    if (!StripeServiceImpl.instance) {
      StripeServiceImpl.instance = new StripeServiceImpl();
    }
    return StripeServiceImpl.instance;
  }

  /**
   * Initialize Stripe with current configuration
   */
  public async initialize(): Promise<boolean> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.isActive || !config.publishableKey) {
        console.warn('Stripe not configured or inactive');
        return false;
      }

      this.stripe = await loadStripe(config.publishableKey);
      
      if (!this.stripe) {
        console.error('Failed to load Stripe');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error initializing Stripe:', error);
      return false;
    }
  }

  /**
   * Get Stripe instance
   */
  public async getStripe(): Promise<Stripe | null> {
    if (!this.stripe) {
      await this.initialize();
    }
    return this.stripe;
  }


  /**
   * Check if we have valid Stripe configuration
   */
  private async hasValidStripeConfig(): Promise<boolean> {
    try {
      const config = await stripeConfigService.getConfig();
      return !!(config && config.publishableKey && config.secretKey && config.isActive);
    } catch {
      return false;
    }
  }

  /**
   * Create payment intent
   */
  public async createPaymentIntent(
    amount: number, 
    currency: string = 'eur', 
    metadata?: Record<string, any>
  ): Promise<PaymentIntentResult> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.secretKey) {
        return {
          success: false,
          error: 'Stripe not configured'
        };
      }
      
      console.log('Using Stripe config:', {
        environment: config.environment,
        hasSecretKey: !!config.secretKey,
        secretKeyPrefix: config.secretKey?.substring(0, 10) + '...',
        hasPublishableKey: !!config.publishableKey,
        secretKeyFormat: config.secretKey?.startsWith('sk_test_') || config.secretKey?.startsWith('sk_live_') ? 'Valid' : 'Invalid'
      });
      
      // Validate secret key format
      if (!config.secretKey.startsWith('sk_test_') && !config.secretKey.startsWith('sk_live_')) {
        console.error('Invalid Stripe secret key format:', config.secretKey.substring(0, 20) + '...');
        return {
          success: false,
          error: 'Invalid Stripe secret key format. Secret key must start with sk_test_ or sk_live_'
        };
      }

      try {
        // Create a real payment intent using Stripe API
        // Build request body
        const params = new URLSearchParams({
          amount: Math.round(amount * 100).toString(), // Convert to cents
          currency: currency
        });
        
        // Add automatic_payment_methods as individual parameters
        params.append('automatic_payment_methods[enabled]', 'true');
        
        // Add customer ID if provided
        if (metadata?.customer_id) {
          params.append('customer', metadata.customer_id);
        }
        
        // Add metadata as individual key-value pairs (excluding customer_id as it's handled above)
        if (metadata) {
          Object.entries(metadata).forEach(([key, value]) => {
            // Only add metadata if value is not undefined or null, and skip customer_id
            if (value !== undefined && value !== null && key !== 'customer_id') {
              params.append(`metadata[${key}]`, String(value));
            }
          });
        }
        
        console.log('Stripe API request body:', params.toString());
        console.log('Amount:', amount, 'Currency:', currency, 'Metadata:', metadata);
        
        const response = await fetch('https://api.stripe.com/v1/payment_intents', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${config.secretKey}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Stripe API error response:', errorData);
          throw new Error(errorData.error?.message || `Stripe API error: ${response.status} ${response.statusText}`);
        }

        const paymentIntent = await response.json();

        return {
          success: true,
          paymentIntentId: paymentIntent.id,
          clientSecret: paymentIntent.client_secret
        };
      } catch (error) {
        console.error('Error creating payment intent:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Failed to create payment intent'
        };
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Confirm payment
   */
  public async confirmPayment(
    paymentIntentId: string, 
    paymentMethodId: string
  ): Promise<PaymentIntentResult> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config) {
        return {
          success: false,
          error: 'Stripe not configured'
        };
      }

      // Use real Stripe Elements for payment confirmation
      const stripe = await this.getStripe();
      
      if (!stripe) {
        return {
          success: false,
          error: 'Stripe not initialized'
        };
      }

      const result = await stripe.confirmPayment({
        clientSecret: paymentIntentId,
        confirmParams: {
          payment_method: paymentMethodId,
          return_url: `${window.location.origin}/checkout/success`
        }
      });

      if (result.error) {
        return {
          success: false,
          error: result.error.message
        };
      }

      const paymentIntent = (result as any).paymentIntent;
      if (!paymentIntent) {
        return {
          success: false,
          error: 'No payment intent returned'
        };
      }

      if (paymentIntent.status === 'requires_action') {
        return {
          success: true,
          requiresAction: true,
          paymentIntentId: paymentIntent.id
        };
      }

      return {
        success: paymentIntent.status === 'succeeded',
        paymentIntentId: paymentIntent.id,
        error: paymentIntent.status !== 'succeeded' ? 'Payment failed' : undefined
      };
    } catch (error) {
      console.error('Error confirming payment:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Wait for payment confirmation and return final status
   */
  public async waitForPaymentConfirmation(
    paymentIntentId: string,
    maxWaitTime: number = 30000 // 30 seconds default
  ): Promise<PaymentIntentResult> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.secretKey) {
        return {
          success: false,
          error: 'Stripe not configured'
        };
      }

      const startTime = Date.now();
      const pollInterval = 2000; // Poll every 2 seconds

      while (Date.now() - startTime < maxWaitTime) {
        try {
          // Fetch payment intent status from Stripe
          const response = await fetch(`https://api.stripe.com/v1/payment_intents/${paymentIntentId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${config.secretKey}`,
            }
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error fetching payment intent:', errorData);
            return {
              success: false,
              error: errorData.error?.message || 'Failed to fetch payment status'
            };
          }

          const paymentIntent = await response.json();
          console.log('Payment intent status:', paymentIntent.status);

          // Check if payment is completed (success or failure)
          if (paymentIntent.status === 'succeeded') {
            return {
              success: true,
              paymentIntentId: paymentIntent.id
            };
          } else if (paymentIntent.status === 'canceled' || paymentIntent.status === 'payment_failed') {
            return {
              success: false,
              error: paymentIntent.last_payment_error?.message || 'Payment failed'
            };
          }

          // If still processing, wait and try again
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        } catch (error) {
          console.error('Error polling payment status:', error);
          // Continue polling unless we've exceeded max wait time
        }
      }

      // Timeout reached
      return {
        success: false,
        error: 'Payment confirmation timeout. Please check your payment status.'
      };
    } catch (error) {
      console.error('Error waiting for payment confirmation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Create refund
   */
  public async createRefund(
    paymentIntentId: string, 
    amount?: number, 
    reason?: string
  ): Promise<RefundInfo> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.secretKey) {
        throw new Error('Stripe not configured');
      }

      // In a real implementation, this would be done on your backend
      const response = await fetch('/api/create-refund', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          amount,
          reason
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create refund');
      }

      const data = await response.json();

      return {
        id: data.refundId,
        amount: data.amount,
        reason: data.reason || 'requested_by_customer',
        status: data.status,
        createdAt: new Date(data.created * 1000),
        notes: data.notes
      };
    } catch (error) {
      console.error('Error creating refund:', error);
      throw error;
    }
  }

  /**
   * Get payment intent details
   */
  public async getPaymentIntent(paymentIntentId: string): Promise<PaymentInfo | null> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.secretKey) {
        return null;
      }

      // In a real implementation, this would be done on your backend
      const response = await fetch(`/api/payment-intent/${paymentIntentId}`);
      
      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      return {
        id: data.id,
        status: this.mapStripeStatus(data.status),
        amount: data.amount,
        currency: data.currency,
        method: this.mapPaymentMethod(data.payment_method?.type),
        last4: data.payment_method?.card?.last4,
        brand: data.payment_method?.card?.brand,
        expMonth: data.payment_method?.card?.exp_month,
        expYear: data.payment_method?.card?.exp_year,
        createdAt: new Date(data.created * 1000),
        updatedAt: new Date(data.updated * 1000)
      };
    } catch (error) {
      console.error('Error getting payment intent:', error);
      return null;
    }
  }

  /**
   * Find existing customer by email
   */
  public async findCustomerByEmail(email: string): Promise<string | null> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.secretKey) {
        return null;
      }

      // Search for existing customer by email
      const response = await fetch(`https://api.stripe.com/v1/customers?email=${encodeURIComponent(email)}&limit=1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${config.secretKey}`,
        }
      });

      if (!response.ok) {
        console.error('Error searching for customer:', await response.text());
        return null;
      }

      const data = await response.json();
      
      if (data.data && data.data.length > 0) {
        console.log('✅ Found existing customer:', data.data[0].id);
        return data.data[0].id;
      }
      
      return null;
    } catch (error) {
      console.error('Error finding customer by email:', error);
      return null;
    }
  }

  /**
   * Create or get existing customer
   */
  public async createOrGetCustomer(customerInfo: CustomerInfo): Promise<string> {
    try {
      // First, try to find existing customer by email
      const existingCustomerId = await this.findCustomerByEmail(customerInfo.email);
      
      if (existingCustomerId) {
        console.log('✅ Using existing customer:', existingCustomerId);
        return existingCustomerId;
      }
      
      // If no existing customer found, create a new one
      console.log('🔄 Creating new customer for email:', customerInfo.email);
      return await this.createCustomer(customerInfo);
    } catch (error) {
      console.error('Error in createOrGetCustomer:', error);
      throw error;
    }
  }

  /**
   * Create customer
   */
  public async createCustomer(customerInfo: CustomerInfo): Promise<string> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.secretKey) {
        throw new Error('Stripe not configured');
      }

      // Create customer using real Stripe API
      const response = await fetch('https://api.stripe.com/v1/customers', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.secretKey}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: (() => {
          const params = new URLSearchParams({
            email: customerInfo.email,
            name: `${customerInfo.firstName} ${customerInfo.lastName}`,
            phone: customerInfo.phone || ''
          });
          
          // Add address fields individually
          if (customerInfo.address?.line1) params.append('address[line1]', customerInfo.address.line1);
          if (customerInfo.address?.line2) params.append('address[line2]', customerInfo.address.line2);
          if (customerInfo.address?.city) params.append('address[city]', customerInfo.address.city);
          if (customerInfo.address?.state) params.append('address[state]', customerInfo.address.state);
          if (customerInfo.address?.postalCode) params.append('address[postal_code]', customerInfo.address.postalCode);
          if (customerInfo.address?.country) params.append('address[country]', customerInfo.address.country);
          
          return params;
        })()
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Stripe customer creation error:', errorData);
        throw new Error(errorData.error?.message || 'Failed to create customer');
      }

      const customer = await response.json();
      return customer.id;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  /**
   * Update customer
   */
  public async updateCustomer(
    customerId: string, 
    customerInfo: Partial<CustomerInfo>
  ): Promise<void> {
    try {
      const config = await stripeConfigService.getConfig();
      
      if (!config || !config.secretKey) {
        throw new Error('Stripe not configured');
      }

      // In a real implementation, this would be done on your backend
      const response = await fetch(`/api/customer/${customerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerInfo)
      });

      if (!response.ok) {
        throw new Error('Failed to update customer');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }

  /**
   * Create payment elements
   */
  public async createPaymentElements(clientSecret: string): Promise<StripeElements | null> {
    try {
      const stripe = await this.getStripe();
      
      if (!stripe) {
        return null;
      }

      this.elements = stripe.elements({
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#ff6b9d',
            colorBackground: '#ffffff',
            colorText: '#30313d',
            colorDanger: '#df1b41',
            fontFamily: 'Inter, system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '8px'
          }
        }
      });

      return this.elements;
    } catch (error) {
      console.error('Error creating payment elements:', error);
      return null;
    }
  }

  /**
   * Get payment element
   */
  public async getPaymentElement(): Promise<StripePaymentElement | null> {
    try {
      if (!this.elements) {
        return null;
      }

      this.paymentElement = this.elements.create('payment');
      return this.paymentElement;
    } catch (error) {
      console.error('Error creating payment element:', error);
      return null;
    }
  }

  /**
   * Mount payment element
   */
  public async mountPaymentElement(containerId: string): Promise<boolean> {
    try {
      if (!this.paymentElement) {
        return false;
      }

      const container = document.getElementById(containerId);
      if (!container) {
        return false;
      }

      await this.paymentElement.mount(`#${containerId}`);
      return true;
    } catch (error) {
      console.error('Error mounting payment element:', error);
      return false;
    }
  }

  /**
   * Unmount payment element
   */
  public async unmountPaymentElement(): Promise<void> {
    try {
      if (this.paymentElement) {
        await this.paymentElement.unmount();
        this.paymentElement = null;
      }
    } catch (error) {
      console.error('Error unmounting payment element:', error);
    }
  }

  /**
   * Destroy elements
   */
  public async destroyElements(): Promise<void> {
    try {
      if (this.elements) {
        this.elements = null;
        this.paymentElement = null;
      }
    } catch (error) {
      console.error('Error destroying elements:', error);
    }
  }

  /**
   * Map Stripe status to our PaymentStatus type
   */
  private mapStripeStatus(stripeStatus: string): PaymentStatus {
    switch (stripeStatus) {
      case 'requires_payment_method':
        return 'requires_payment_method';
      case 'requires_confirmation':
        return 'requires_confirmation';
      case 'requires_action':
        return 'requires_action';
      case 'processing':
        return 'processing';
      case 'succeeded':
        return 'succeeded';
      case 'canceled':
        return 'canceled';
      default:
        return 'failed';
    }
  }

  /**
   * Map Stripe payment method to our PaymentMethod type
   */
  private mapPaymentMethod(stripeType: string): PaymentMethod {
    switch (stripeType) {
      case 'card':
        return 'card';
      case 'bank_transfer':
        return 'bank_transfer';
      default:
        return 'other';
    }
  }

  /**
   * Check if Stripe is properly configured
   */
  public async isConfigured(): Promise<boolean> {
    try {
      const config = await stripeConfigService.getConfig();
      return !!(config && config.isActive && config.publishableKey && config.secretKey);
    } catch (error) {
      return false;
    }
  }

  /**
   * Get configuration status
   */
  public async getConfigStatus(): Promise<{
    isConfigured: boolean;
    environment: 'test' | 'live' | null;
    isActive: boolean;
  }> {
    try {
      const status = await stripeConfigService.getConfigStatus();
      return {
        isConfigured: status.isConfigured,
        environment: status.environment,
        isActive: status.isActive
      };
    } catch (error) {
      return {
        isConfigured: false,
        environment: null,
        isActive: false
      };
    }
  }
}

// Export singleton instance
export const stripeService = StripeServiceImpl.getInstance();

// Export class for testing
export { StripeServiceImpl };
